import { UserType } from '../types/userTypes'
import {
  ConfiguredProduct,
  ConfiguredProductListItem,
  isConfiguredProductListItem,
  isRoom,
  ListRoom
} from '../list/ListTypes'

import { isAnyObject, isAString, isOneOf } from '../utils/typeChecking'
import { ProductSeries } from '../types/productTypes'

export interface UserAppState {
  configuredProducts: readonly Readonly<ConfiguredProductListItem>[]
  rooms: readonly ListRoom[]
  wallColor: string
  moreInfo: boolean
  userType: UserType | null
  listName: string
  listId: string
  renameListTutorialDismissed: boolean
  typeSelectorTutorialDismissed: boolean
  hasListBeenShown: boolean
  customiseHintShownCount: number
  selectedProductSeries: ProductSeries | null
  isAuthenticated: boolean
}

export function isUserAppState(value: unknown): value is UserAppState {
  if (!isAnyObject(value)) {
    return false
  }

  const state: UserAppState = value as UserAppState

  // noinspection SuspiciousTypeOfGuard
  return (
    // configuredProducts: readonly Readonly<ConfiguredProductListItem>[]
    Array.isArray(state.configuredProducts)
    && !state.configuredProducts.find(it => !isConfiguredProductListItem(it))
    // rooms: readonly ListRoom[]
    && Array.isArray(state.rooms)
    && !state.rooms.find(it => !isRoom(it))
    // wallColor: string
    && isAString(state.wallColor)
    // moreInfo: boolean
    && typeof state.moreInfo === 'boolean'
    // userType: UserType | null
    && (
      isOneOf(
        state.userType,
        UserType.Consumer,
        UserType.Electrician,
        null,
      )
    )
    // listName: string
    && isAString(state.listName)
    // renameListTutorialDismissed: boolean
    && typeof state.renameListTutorialDismissed === 'boolean'
    // typeSelectorTutorialDismissed: boolean
    && typeof state.typeSelectorTutorialDismissed === 'boolean'
    // hasListBeenShown: boolean
    && typeof state.hasListBeenShown === 'boolean'
    // customiseHintShownCount: number
    && typeof state.customiseHintShownCount === 'number'
    // selectedProductSeries: 'Iconic' | 'Pro'
    && isOneOf(state.selectedProductSeries, ProductSeries.Iconic, ProductSeries.Pro, null)
    // isAuthenticated: boolean
    && typeof state.isAuthenticated === 'boolean'
  )
}

export enum WiserOrMomentary {
  Wiser = 'Wiser',
  MomentarySwitches = 'MomentarySwitches'
}

export interface WiserMode {
  type: WiserOrMomentary
  mechOverride: string | null
}

export interface UiAppState {
  screen: 'list' | 'sharingList' | 'sharingBom' | 'wallColor' | null
  editingProductId: string
  isEditing: boolean
  focusedListItemUid: string | null
  debugMode: boolean
  showCashSales: boolean
  hasOptedOutOfWiserHubPrompt: boolean
  hasOptedOutOfMomentarySwitchesPrompt: boolean
  optionalWiserMode: WiserMode | null
}

export interface AppState extends UiAppState {
  userState: UserAppState
}

export enum AppStateActions {
  SaveProduct = 'SaveProduct',
  NewProduct = 'NewProduct',
  EditProduct = 'EditProduct',
  DuplicateProduct = 'DuplicateProduct',
  ToggleListDisplayed = 'ToggleListDisplayed',
  ToggleMoreInfo = 'ToggleMoreInfo',
  SetListDisplayed = 'SetListDisplayed',
  OpenListFromBanner = 'OpenListFromBanner',
  CancelEditing = 'CancelEditing',
  ChangeQuantity = 'ChangeQuantity',
  ChangeNote = 'ChangeNote',
  ChangeRoom = 'ChangeRoom',
  AddRoom = 'AddRoom',
  DeleteRoom = 'DeleteRoom',
  RenameRoom = 'RenameRoom',
  SelectUserType = 'SelectUserType',
  RenameList = 'RenameList',
  ClearList = 'ClearList',
  OpenSharingList = 'OpenSharingList',
  OpenSharingBom = 'OpenSharingBom',
  OpenWallColor = 'OpenWallColor',
  ChangeWallColor = 'ChangeWallColor',
  SetDebugEnabled = 'SetDebugEnabled',
  RoomExpanded = 'RoomExpanded',
  RenameListTutorialDismissed = 'RenameListTutorialDismissed',
  TypeSelectorTutorialDismissed = 'TypeSelectorTutorialDismissed',
  CustomiseHintShown = 'CustomiseHintShown',
  CashSalesVisibilityChanged = 'CashSalesVisibilityChanged',
  WiserModeChanged = 'WiserModeChanged',
  ProductSeriesChanged = 'ProductSeriesChanged',
  ToggleIsAuthenticated = 'ToggleIsAuthenticated',
  DisableWiserHubPrompt = 'DisableWiserHubPrompt',
  DisableMomentarySwitchesPrompt = 'DisableMomentarySwitchesPrompt',
}

export type AppReducerAction = {
  action: AppStateActions.ToggleMoreInfo
} | {
  action: AppStateActions.SaveProduct
  product: ConfiguredProduct
} | {
  action: AppStateActions.NewProduct
} | {
  action: AppStateActions.ToggleListDisplayed
} | {
  action: AppStateActions.SetListDisplayed
  displayed: boolean
} | {
  action: AppStateActions.EditProduct
  uid: string
} | {
  action: AppStateActions.DuplicateProduct
  uid: string
} | {
  action: AppStateActions.CancelEditing
} | {
  action: AppStateActions.ChangeQuantity
  uid: string
  newQuantity: number
} | {
  action: AppStateActions.ChangeNote
  listItemUid: string
  newNote: string
} | {
  action: AppStateActions.ChangeRoom
  listItemUid: string
  roomId: string | null
} | {
  action: AppStateActions.AddRoom
  name: string
} | {
  action: AppStateActions.RenameRoom
  name: string
  uid: string
} | {
  action: AppStateActions.DeleteRoom
  uid: string
  keepItems: boolean
} | {
  action: AppStateActions.OpenListFromBanner
  uid: string
} | {
  action: AppStateActions.SelectUserType
  userType: UserType
} | {
  action: AppStateActions.RenameList
  newName: string
} | {
  action: AppStateActions.ClearList
  newListName: string
  newListId: string
} | {
  action: AppStateActions.OpenSharingList
} | {
  action: AppStateActions.OpenSharingBom
} | {
  action: AppStateActions.OpenWallColor
} | {
  action: AppStateActions.ChangeWallColor
  color: string
} | {
  action: AppStateActions.SetDebugEnabled
  enabled: boolean
} | {
  action: AppStateActions.RoomExpanded
  roomUid: string
  expanded: boolean
} | {
  action: AppStateActions.RenameListTutorialDismissed
} | {
  action: AppStateActions.TypeSelectorTutorialDismissed
} | {
  action: AppStateActions.CustomiseHintShown
} | {
  action: AppStateActions.CashSalesVisibilityChanged
  visible: boolean
} | {
  action: AppStateActions.WiserModeChanged
  newMode: WiserMode | null
} | {
  action: AppStateActions.ProductSeriesChanged
  series: ProductSeries
} | {
  action: AppStateActions.ToggleIsAuthenticated
} | {
  action: AppStateActions.DisableWiserHubPrompt
} | {
  action: AppStateActions.DisableMomentarySwitchesPrompt
}

